import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import './CollegeAdminDashboard.css';

const PAGE_LIMIT = 10;
const ASSIGN_PAGE_LIMIT = 10;

const BuildingIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

const UsersIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const Pagination = ({ page, totalPages, total, limit, onPageChange }) => {
  if (totalPages <= 1) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const getRange = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (page > 3) pages.push('\u2026');
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
    if (page < totalPages - 2) pages.push('\u2026');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="ca-pagination">
      <span className="ca-pagination-info">Showing {start}\u2013{end} of {total}</span>
      <div className="ca-pagination-controls">
        <button className="ca-page-btn" onClick={() => onPageChange(page - 1)} disabled={page === 1}>\u2039</button>
        {getRange().map((p, i) =>
          p === '\u2026' ? (
            <span key={`e-${i}`} className="ca-page-ellipsis">\u2026</span>
          ) : (
            <button
              key={p}
              className={`ca-page-btn ${p === page ? 'ca-page-btn--active' : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}
        <button className="ca-page-btn" onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>\u203a</button>
      </div>
    </div>
  );
};

const CollegeAdminDepartments = () => {
  const { user } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState('departments');

  // Department management state
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDept, setNewDept] = useState({ department_name: '', department_code: '' });

  // Department assignment state
  const [hodUsers, setHodUsers] = useState([]);
  const [availableDepts, setAvailableDepts] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignPage, setAssignPage] = useState(1);
  const [assignMeta, setAssignMeta] = useState({ total: 0, totalPages: 0 });
  const [assignLoading, setAssignLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  // Shared state
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // --- Department Management ---

  const fetchDepartments = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await API.get(
        `/public/departments?college_id=${user.college_id}&page=${p}&limit=${PAGE_LIMIT}`
      );
      setDepartments(data.departments || []);
      setMeta({ total: data.total ?? 0, totalPages: data.totalPages ?? 1 });
    } catch {
      showToast('Failed to load departments', 'error');
    } finally {
      setLoading(false);
    }
  }, [user.college_id]);

  useEffect(() => {
    if (activeTab === 'departments') {
      fetchDepartments(1);
    }
  }, [activeTab, fetchDepartments]);

  const handlePageChange = (p) => { setPage(p); fetchDepartments(p); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const { data } = await API.post('/auth/create-department', {
        ...newDept,
        college_id: user.college_id
      });
      if (data.success) {
        showToast('Department created');
        setNewDept({ department_name: '', department_code: '' });
        setShowCreateModal(false);
        setPage(1);
        fetchDepartments(1);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create department', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleDeptStatus = async (deptId, currentStatus) => {
    setStatusLoadingId(deptId);
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const { data } = await API.patch(`/departments/${deptId}/status`, { status: newStatus });
      if (data.success) {
        setDepartments(prev => prev.map(d =>
          d.department_id === deptId ? { ...d, status: newStatus } : d
        ));
        showToast(`Department ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update department status', 'error');
    } finally {
      setStatusLoadingId(null);
    }
  };

  // --- Department Assignment ---

  const fetchHodUsers = useCallback(async (p = 1, search = '') => {
    setAssignLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: ASSIGN_PAGE_LIMIT });
      if (search) params.append('search', search);

      const { data } = await API.get(`/departments/hod-users?${params}`);
      setHodUsers(data.users || []);
      setAvailableDepts(data.departments || []);
      setAssignMeta({ total: data.total ?? 0, totalPages: data.totalPages ?? 1 });
    } catch {
      showToast('Failed to load HOD users', 'error');
    } finally {
      setAssignLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'assignment') {
      fetchHodUsers(1, assignSearch);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignSearch = (e) => {
    const val = e.target.value;
    setAssignSearch(val);
    setAssignPage(1);
    fetchHodUsers(1, val);
  };

  const handleAssignPageChange = (p) => {
    setAssignPage(p);
    fetchHodUsers(p, assignSearch);
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedDeptId(user.department_id ? String(user.department_id) : '');
    setShowAssignModal(true);
  };

  const handleSaveDepartment = async () => {
    if (!selectedDeptId) {
      showToast('Please select a department', 'error');
      return;
    }
    setAssignSaving(true);
    try {
      const { data } = await API.put(`/departments/users/${selectedUser.user_id}/department`, {
        department_id: parseInt(selectedDeptId)
      });
      if (data.success) {
        showToast(`Department assigned to ${selectedUser.name}`);
        setShowAssignModal(false);
        setSelectedUser(null);
        fetchHodUsers(assignPage, assignSearch);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to assign department', 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const tabStyle = (tab) => ({
    borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
    color: activeTab === tab ? '#3b82f6' : '#64748b',
    fontWeight: activeTab === tab ? 600 : 500,
    cursor: 'pointer',
    padding: '0.75rem 1.25rem',
    fontSize: '0.9rem',
    background: 'none',
    borderTop: 'none',
    borderRight: 'none',
    borderLeft: 'none',
    transition: 'all 0.2s',
    fontFamily: 'inherit'
  });

  return (
    <div className="ca-page">
      {toast && <div className={`ca-toast ca-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="ca-page-header">
        <h1>Departments</h1>
        <p>Manage departments and assignments in your college</p>
      </div>

      <div className="ca-tab-content">
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
          <button style={tabStyle('departments')} onClick={() => setActiveTab('departments')}>
            Department Management
          </button>
          <button style={tabStyle('assignment')} onClick={() => setActiveTab('assignment')}>
            Department Assignment
          </button>
        </div>

        {/* Departments Tab */}
        {activeTab === 'departments' && (
          <>
            <div className="ca-section-header">
              <div>
                <h2>Department Management</h2>
                {meta.total > 0 && (
                  <p>{meta.total} department{meta.total !== 1 ? 's' : ''} total</p>
                )}
              </div>
              <button className="ca-btn-primary" onClick={() => setShowCreateModal(true)}>
                + New Department
              </button>
            </div>

            {loading ? (
              <div className="ca-spinner-wrap"><div className="ca-spinner" /></div>
            ) : departments.length === 0 ? (
              <div className="ca-empty">
                <BuildingIcon />
                <h3>No departments yet</h3>
                <p>Create your first department to get started.</p>
              </div>
            ) : (
              <>
                <div className="ca-table-wrap">
                  <table className="ca-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Code</th>
                        <th>HOD</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map(dept => (
                        <tr key={dept.department_id}>
                          <td className="ca-td-name">{dept.department_name}</td>
                          <td><span className="ca-code-tag">{dept.department_code}</span></td>
                          <td>
                            {dept.hod_name
                              ? dept.hod_name
                              : dept.hod_user_id
                                ? 'Assigned'
                                : <span className="ca-muted">Not assigned</span>}
                          </td>
                          <td>
                            <span className={`ca-status-badge ca-status-badge--${dept.status || 'active'}`}>
                              {dept.status || 'active'}
                            </span>
                          </td>
                          <td>
                            {(dept.status === 'active' || dept.status === 'inactive') && (
                              <button
                                className={`ca-action-btn ${dept.status === 'active' ? 'ca-action-btn--deactivate' : 'ca-action-btn--activate'}`}
                                disabled={statusLoadingId === dept.department_id}
                                onClick={() => toggleDeptStatus(dept.department_id, dept.status)}
                              >
                                {statusLoadingId === dept.department_id ? (
                                  <span className="ca-action-spinner" />
                                ) : dept.status === 'active' ? (
                                  'Deactivate'
                                ) : (
                                  'Activate'
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  totalPages={meta.totalPages}
                  total={meta.total}
                  limit={PAGE_LIMIT}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </>
        )}

        {/* Assignment Tab */}
        {activeTab === 'assignment' && (
          <>
            <div className="ca-section-header">
              <div>
                <h2>Department Assignment</h2>
                <p>Assign HOD users to departments</p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search HOD users..."
                  value={assignSearch}
                  onChange={handleAssignSearch}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.875rem',
                    width: '220px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {assignLoading ? (
              <div className="ca-spinner-wrap"><div className="ca-spinner" /></div>
            ) : hodUsers.length === 0 ? (
              <div className="ca-empty">
                <UsersIcon />
                <h3>No HOD users found</h3>
                <p>{assignSearch ? 'No HOD users matching your search.' : 'No HOD users exist in your college yet.'}</p>
              </div>
            ) : (
              <>
                <div className="ca-table-wrap">
                  <table className="ca-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Current Department</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hodUsers.map(user => (
                        <tr key={user.user_id}>
                          <td className="ca-td-name">{user.name}</td>
                          <td><a href={`mailto:${user.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{user.email}</a></td>
                          <td>
                            {user.department_name
                              ? <strong>{user.department_name}</strong>
                              : <span style={{ color: '#f59e0b' }}>Not assigned</span>
                            }
                          </td>
                          <td>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: user.status === 'active' ? '#d1fae5' : '#fee2e2',
                              color: user.status === 'active' ? '#065f46' : '#991b1b'
                            }}>
                              {user.status || 'active'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="ca-btn-approve"
                              onClick={() => openAssignModal(user)}
                            >
                              {user.department_id ? 'Change' : 'Assign'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={assignPage}
                  totalPages={assignMeta.totalPages}
                  total={assignMeta.total}
                  limit={ASSIGN_PAGE_LIMIT}
                  onPageChange={handleAssignPageChange}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Create Department Modal */}
      {showCreateModal && (
        <div className="ca-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h3>New Department</h3>
              <button className="ca-modal-close" onClick={() => setShowCreateModal(false)}>{'\u00d7'}</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="ca-modal-body">
                <div className="ca-form-group">
                  <label className="ca-form-label">Department Name *</label>
                  <input
                    type="text"
                    className="ca-form-input"
                    value={newDept.department_name}
                    onChange={e => setNewDept(d => ({ ...d, department_name: e.target.value }))}
                    placeholder="e.g., Computer Science"
                    required
                    autoFocus
                  />
                </div>
                <div className="ca-form-group">
                  <label className="ca-form-label">Department Code *</label>
                  <input
                    type="text"
                    className="ca-form-input"
                    value={newDept.department_code}
                    onChange={e => setNewDept(d => ({ ...d, department_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., CSE"
                    required
                  />
                </div>
              </div>
              <div className="ca-modal-footer">
                <button type="button" className="ca-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="ca-btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Creating\u2026' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Assignment Modal */}
      {showAssignModal && selectedUser && (
        <div className="ca-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="ca-modal" onClick={e => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h3>Assign Department to {selectedUser.name}</h3>
              <button className="ca-modal-close" onClick={() => setShowAssignModal(false)}>{'\u00d7'}</button>
            </div>
            <div className="ca-modal-body">
              <div className="ca-form-group">
                <label className="ca-form-label">Select Department</label>
                <select
                  className="ca-form-input"
                  value={selectedDeptId}
                  onChange={e => setSelectedDeptId(e.target.value)}
                  required
                >
                  <option value="">-- Select Department --</option>
                  {availableDepts.map(d => (
                    <option key={d.department_id} value={d.department_id}>
                      {d.department_name} ({d.department_code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ca-modal-footer">
              <button
                type="button"
                className="ca-btn-secondary"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ca-btn-primary"
                onClick={handleSaveDepartment}
                disabled={assignSaving || !selectedDeptId}
              >
                {assignSaving ? 'Saving\u2026' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeAdminDepartments;
